import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDeletePricingPlanAdjustment } from 'src/features/pricingPlan/api/adjustments/useDeletePricingPlanAdjustment';
import { LIST_PRICING_PLAN_PERIOD_ADJUSTMENTS_RESOURCE } from 'src/features/pricingPlan/api/adjustments/useListPricingPlanPeriodAdjustments';
import { PricingPlanPeriodAdjustment } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface PricingPlanAdjustmentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAdjustment: PricingPlanPeriodAdjustment;
    children?: React.ReactNode;
}

function DeletePricingPlanTickRateFormModal<TData>({
    selectedAdjustment,
    children,
}: PricingPlanAdjustmentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate: deleteAdjustment } = useDeletePricingPlanAdjustment({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_PERIOD_ADJUSTMENTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.deleteAdjustmentSuccess),
                description: formatMessage(pricingPlanMessages.deleteAdjustmentSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...pricingPlanMessages.deleteAdjustment },
                subTitle: { ...pricingPlanMessages.deleteAdjustmentSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...pricingPlanMessages.confirmDeleteAdjustment },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: pricingPlanMessages.deleteAdjustment,
                callbackFn: () => {
                    return (
                        deleteAdjustment({
                            planId,
                            parkingId,
                            adjustmentId: selectedAdjustment.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeletePricingPlanTickRateFormModal;
